import React from 'react'
import * as rn from 'react-native'

import * as c from '../../common'
import * as r from '../../react-utils'

export type CCTVSimProps = c.r<{
  /**
   * `aspectRatio` is fucked in React Native, need to calculate the sizes
   * manually for each pic.
   */
  containerHeight: number
  /**
   * `aspectRatio` is fucked in React Native, need to calculate the sizes
   * manually for each pic.
   */
  containerWidth: number
  customerID: string
}>

export default React.memo<CCTVSimProps>(
  ({ customerID, containerHeight, containerWidth }) => {
    // Derived rather than passed in. Only the 8-camera grid cares - four across
    // is right for a box that is wider than it is tall, and on an iPad held
    // upright (which cannot be rotated out of, see CCTVSimScreen) it would be
    // four columns the width of a finger. It follows entirely from the other
    // two props, so making it a prop would only create a way to get it wrong.
    const horizontal = containerWidth > containerHeight
    const [{ security_cams_is_6: camsIs6 = '4' }] = r.useCustomer(customerID)
    // `security_cams_is_6` predates the 8-camera tier and was a YesNo field
    // — 'yes' meant 6 cameras, 'no'/empty meant 4. Keep interpreting those
    // old values the same way for customers saved before this became a
    // 4/6/8 picker.
    const camCount =
      camsIs6 === '8' ? '8' : camsIs6 === '6' || camsIs6 === 'yes' ? '6' : '4'

    const selectAllCustomerMedia = React.useMemo(
      (): ReturnType<typeof c.makeSelectAllCustomerMedia> =>
        c.makeSelectAllCustomerMedia(),
      [],
    )
    const selectAllCustomerMediaArgs = React.useMemo(
      (): c.SelectMediaParams => ({
        customerID,
        mediaKind: 'security_cam0',
      }),
      [customerID],
    )
    const allCustomerMedia = c.useSelector((_) =>
      selectAllCustomerMedia(_, selectAllCustomerMediaArgs),
    )
    const camMedia0 = c.values(allCustomerMedia.security_cam0)[0]
    const camSrc0 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia0 ? c.getMediaItemSrc(camMedia0) : '',
      }),
      [camMedia0],
    )
    const camMedia1 = c.values(allCustomerMedia.security_cam1)[0]
    const camSrc1 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia1 ? c.getMediaItemSrc(camMedia1) : '',
      }),
      [camMedia1],
    )
    const camMedia2 = c.values(allCustomerMedia.security_cam2)[0]
    const camSrc2 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia2 ? c.getMediaItemSrc(camMedia2) : '',
      }),
      [camMedia2],
    )
    const camMedia3 = c.values(allCustomerMedia.security_cam3)[0]
    const camSrc3 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia3 ? c.getMediaItemSrc(camMedia3) : '',
      }),
      [camMedia3],
    )
    const camMedia4 = c.values(allCustomerMedia.security_cam4)[0]
    const camSrc4 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia4 ? c.getMediaItemSrc(camMedia4) : '',
      }),
      [camMedia4],
    )
    const camMedia5 = c.values(allCustomerMedia.security_cam5)[0]
    const camSrc5 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia5 ? c.getMediaItemSrc(camMedia5) : '',
      }),
      [camMedia5],
    )
    const camMedia6 = c.values(allCustomerMedia.security_cam6)[0]
    const camSrc6 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia6 ? c.getMediaItemSrc(camMedia6) : '',
      }),
      [camMedia6],
    )
    const camMedia7 = c.values(allCustomerMedia.security_cam7)[0]
    const camSrc7 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia7 ? c.getMediaItemSrc(camMedia7) : '',
      }),
      [camMedia7],
    )

    const wrapperStyle = React.useMemo(
      (): rn.ViewStyle => ({ height: containerHeight, width: containerWidth }),
      [containerHeight, containerWidth],
    )

    const containerStyle = React.useMemo(
      (): rn.ViewStyle => ({
        backgroundColor: '#000000',
        flexDirection: 'row',
        flexWrap: 'wrap',
        height: containerHeight,
        width: containerWidth,
      }),
      [containerHeight, containerWidth],
    )

    const imgStyle4 = React.useMemo(
      (): rn.ImageStyle => ({
        height: Math.floor(containerHeight * (1 / 2)),
        width: Math.floor(containerWidth * (1 / 2)),
      }),
      [containerHeight, containerWidth],
    )
    const imgStyle6Big = React.useMemo(
      (): rn.ImageStyle => ({
        height: Math.floor(containerHeight * (2 / 3)),
        width: Math.floor(containerWidth * (2 / 3)),
      }),
      [containerHeight, containerWidth],
    )
    const imgStyle6Small = React.useMemo(
      (): rn.ImageStyle => ({
        height: Math.floor(containerHeight * (1 / 3)),
        width: Math.floor(containerWidth * (1 / 3)),
      }),
      [containerHeight, containerWidth],
    )
    const containerStyle6Vertical = React.useMemo(
      (): rn.ViewStyle => ({
        flexDirection: 'column',
        height: Math.floor(containerHeight * (2 / 3)),
        width: Math.floor(containerWidth * (1 / 3)),
      }),
      [containerHeight, containerWidth],
    )
    // 4 across x 2 down when the box is wide, 2 across x 4 down when it is
    // tall. Either way the eight cells tile the box exactly.
    const imgStyle8 = React.useMemo(
      (): rn.ImageStyle => ({
        height: Math.floor(containerHeight * (horizontal ? 1 / 2 : 1 / 4)),
        width: Math.floor(containerWidth * (horizontal ? 1 / 4 : 1 / 2)),
      }),
      [containerHeight, containerWidth, horizontal],
    )

    // Black cells with nothing on them read as a broken screen (13438750441).
    // Only the cells this layout draws count: a photo in cam 6 of a customer now
    // on 4 cameras is not on screen.
    const hasPhotos = [
      camMedia0,
      camMedia1,
      camMedia2,
      camMedia3,
      camMedia4,
      camMedia5,
      camMedia6,
      camMedia7,
    ]
      .slice(0, Number(camCount))
      .some(Boolean)

    const [loadFailed, setLoadFailed] = React.useState(false)
    // Remounts the images: <Image> does not ask again for a uri that failed.
    const [loadAttempt, setLoadAttempt] = React.useState(0)
    const handleImgError = React.useCallback((): void => {
      setLoadFailed(true)
    }, [])
    const handleRetry = React.useCallback((): void => {
      setLoadFailed(false)
      setLoadAttempt((n) => n + 1)
    }, [])

    const baseNode = (() => {
      if (camCount === '4') {
        return (
          <rn.View style={containerStyle}>
            <rn.Image
              resizeMode="cover"
              style={imgStyle4}
              source={camSrc0}
              onError={camMedia0 ? handleImgError : undefined}
            />
            <rn.Image
              resizeMode="cover"
              style={imgStyle4}
              source={camSrc1}
              onError={camMedia1 ? handleImgError : undefined}
            />
            <rn.Image
              resizeMode="cover"
              style={imgStyle4}
              source={camSrc2}
              onError={camMedia2 ? handleImgError : undefined}
            />
            <rn.Image
              resizeMode="cover"
              style={imgStyle4}
              source={camSrc3}
              onError={camMedia3 ? handleImgError : undefined}
            />
          </rn.View>
        )
      }

      if (camCount === '6') {
        return (
          <rn.View style={containerStyle}>
            <rn.Image
              resizeMode="cover"
              style={imgStyle6Big}
              source={camSrc0}
              onError={camMedia0 ? handleImgError : undefined}
            />

            <rn.View style={containerStyle6Vertical}>
              <rn.Image
                resizeMode="cover"
                style={imgStyle6Small}
                source={camSrc1}
                onError={camMedia1 ? handleImgError : undefined}
              />
              <rn.Image
                resizeMode="cover"
                style={imgStyle6Small}
                source={camSrc2}
                onError={camMedia2 ? handleImgError : undefined}
              />
            </rn.View>

            <rn.Image
              resizeMode="cover"
              style={imgStyle6Small}
              source={camSrc3}
              onError={camMedia3 ? handleImgError : undefined}
            />
            <rn.Image
              resizeMode="cover"
              style={imgStyle6Small}
              source={camSrc4}
              onError={camMedia4 ? handleImgError : undefined}
            />
            <rn.Image
              resizeMode="cover"
              style={imgStyle6Small}
              source={camSrc5}
              onError={camMedia5 ? handleImgError : undefined}
            />
          </rn.View>
        )
      }

      return (
        <rn.View style={containerStyle}>
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc0}
            onError={camMedia0 ? handleImgError : undefined}
          />
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc1}
            onError={camMedia1 ? handleImgError : undefined}
          />
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc2}
            onError={camMedia2 ? handleImgError : undefined}
          />
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc3}
            onError={camMedia3 ? handleImgError : undefined}
          />
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc4}
            onError={camMedia4 ? handleImgError : undefined}
          />
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc5}
            onError={camMedia5 ? handleImgError : undefined}
          />
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc6}
            onError={camMedia6 ? handleImgError : undefined}
          />
          <rn.Image
            resizeMode="cover"
            style={imgStyle8}
            source={camSrc7}
            onError={camMedia7 ? handleImgError : undefined}
          />
        </rn.View>
      )
    })()

    return (
      <rn.View key={loadAttempt} style={wrapperStyle}>
        {baseNode}

        {!hasPhotos && (
          <rn.View pointerEvents="box-none" style={noticeStyle}>
            <rn.Text style={noticeTitleStyle}>No camera photos yet</rn.Text>
            <rn.Text style={noticeBodyStyle}>
              Add photos to the Cam angle fields in this customer's CCTV
              Simulator section to see them here.
            </rn.Text>
          </rn.View>
        )}

        {hasPhotos && loadFailed && (
          <rn.View pointerEvents="box-none" style={noticeStyle}>
            <rn.Text style={noticeTitleStyle}>
              Some camera photos did not load
            </rn.Text>
            <rn.Pressable onPress={handleRetry} style={retryStyle}>
              <rn.Text style={noticeBodyStyle}>Retry</rn.Text>
            </rn.Pressable>
          </rn.View>
        )}
      </rn.View>
    )
  },
)

const noticeStyle: rn.ViewStyle = {
  ...rn.StyleSheet.absoluteFillObject,
  alignItems: 'center',
  justifyContent: 'center',
  padding: 32,
}

const noticeTitleStyle: rn.TextStyle = {
  color: '#FFFFFF',
  fontFamily: c.light.fontFamily,
  fontSize: 22,
  fontWeight: 'bold',
  marginBottom: 8,
  textAlign: 'center',
}

const noticeBodyStyle: rn.TextStyle = {
  color: '#FFFFFF',
  fontFamily: c.light.fontFamily,
  fontSize: 16,
  textAlign: 'center',
}

const retryStyle: rn.ViewStyle = {
  borderColor: '#FFFFFF',
  borderRadius: 8,
  borderWidth: 1,
  marginTop: 8,
  paddingHorizontal: 24,
  paddingVertical: 12,
}
